import { merge } from 'lodash';
import React, { useMemo, useCallback } from 'react';

import { ProjectDateReminderSortFields, SortDirection, GetProjectDateRemindersQueryVariables } from '@/graphql';
import { BaseTableProps } from '@boilerplate/types/entity';
import Entity from '@/entities/projectDateReminder';
import EntityTable, {
  defaultPage,
  defaultPageSize,
  defaultSortOrder,
  getDefaultSortField,
} from '@boilerplate/components/entity/EntityTable/EntityTable';
import { canPerformAction, useAuthenticatedUserStore } from '@/stores/UserStore';
import { openConfirmationDialog } from '@/stores/ConfirmationDialogStore';


export const useProjectDateRemindersTable = ({ queryOptions }: { queryOptions?: GetProjectDateRemindersQueryVariables } = {}) => {
  const defaultVariableOptions = useMemo<GetProjectDateRemindersQueryVariables>(
    () => 
      merge(
        {
          sorting: {
            direction: defaultSortOrder === 'desc' ? SortDirection.Desc : SortDirection.Asc,
            field: getDefaultSortField(Entity.table.schema) as ProjectDateReminderSortFields,
          },
          paging: {
            page: defaultPage,
            pageSize: defaultPageSize,
          },
                  withProjectDate: true,
                  withRoles: true,
          },
          queryOptions,
        ), 
    [queryOptions]
    );
  
  const {
    data: projectDateReminderData,
    items = [],
    loading,
    refetch: originalRefetch,
    variables,
  } = Entity.model.useGetAll({
    fetchPolicy: 'cache-and-network',
    variables: defaultVariableOptions,
  });

  const [remove] = Entity.model.useDelete();

  const refetch = useCallback(
    (variableOptions?: Partial<GetProjectDateRemindersQueryVariables >) => {
      return originalRefetch(merge({}, defaultVariableOptions, variableOptions));
    },
    [defaultVariableOptions, originalRefetch]
  );

  const allActions = useMemo(() => ({ loading, refetch, remove }), [loading, refetch, remove]);
  const data = useMemo(
    () =>
      projectDateReminderData
        ? { totalCount: projectDateReminderData.projectDateReminders.totalCount, rows: items }
        : { totalCount: 0, rows: [] },
    [projectDateReminderData, items]
  );

  return { data, actions: allActions, refetch, defaultVariableOptions, variables };
}

export type ProjectDateRemindersBaseTableProps = BaseTableProps<typeof Entity.model>;

export default function ProjectDateRemindersBaseTable(props: ProjectDateRemindersBaseTableProps) {
  const tableProps = useProjectDateRemindersTable();
  const authentictedUser = useAuthenticatedUserStore();

  return (
    <EntityTable
      {...props}
      {...tableProps}
      Entity={Entity}
      user={authentictedUser}
      canPerformThisAction={canPerformAction}
      openConfirmationDialog={openConfirmationDialog}
    />
  );
}
